import type { ListColumn } from "../../../../../shared/frontend/list/listColumn";
import { Form, Input, InputNumber, Select, Space, Typography } from "../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../shared/frontend/components/ControlledPickers";
import dayjs,{ Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useFormDirty } from "../app/utils/useFormDirty";
import { useDirtySnapshot } from "../../../../../shared/frontend/forms/useDirtySnapshot";
import { useAuth } from "../app/auth/useAuth";
import { OPEN_ITEM_STATUS_OPEN } from "../app/constants/openItemStatus";
import { useI18n } from "../app/i18n/useI18n";
import { useDateInputFormat } from "../app/units/DateInputFormatContext";
import {
decimalSeparatorOf,
formatDateOnly,
formatMoney,
moneyPlaceholderOf
} from "../app/units/format";
import { useLastPaymentMethod } from "../app/prefs/useLastPaymentMethod";
import { loadUnits } from "../app/units/unitsStorage";
import { paymentMethodDefinitionSelectOptions } from "../app/utils/paymentMethod";
import { MANUAL_PAYMENT_METHOD_TYPES, PAYMENT_METHOD_DEFAULT } from "../app/utils/paymentMethodCodes";
import { formatDateOnlyIso } from "../app/utils/formatDateOnlyIso";
import { DrawerSelectionList } from "./DrawerSelectionList";
import { FormDrawer } from "./FormDrawer";
import { useFeedback } from "../../../../../shared/frontend/feedback/useFeedback";
import { WALLET_TYPE_VALUE } from "../app/constants/walletType";
import { ISO_DATE_LENGTH } from "../app/utils/isoDate";

// Spaltenbreiten der Auswahlliste
const DESCRIPTION_COLUMN_WIDTH = 200;
const DUE_DATE_COLUMN_WIDTH = 140;
const AMOUNT_COLUMN_WIDTH = 120;
/** Umgang mit einer Differenz zum Sollbetrag (Formularwert differenceHandling). */
const DIFFERENCE_HANDLING_NEXT_DUE = "nextDue";
const DIFFERENCE_HANDLING_WALLET = "wallet";
const ITEM_TYPE_CONTRIBUTION = "contribution";
// Halber Cent als Float-Toleranz: Differenzen/Beträge ab 1 Cent werden erkannt,
// reines Float-Rauschen (< 0,005) wird ignoriert.
const MONEY_EPSILON = 0.005;

// Bewusst modul-lokal: Ein Nicht-Komponenten-Export bricht in dieser Datei Fast Refresh
// (react-refresh/only-export-components). Wird die Funktion außerhalb gebraucht, gehört sie
// in ein eigenes Modul unter app/utils/.
/**
 * Häufigste Zahlart der gewählten Posten. Das ist die *geplante* Zahlart des Postens, nicht die
 * tatsächlich geflossene — sie dient nur als letzte Vorbelegung, wenn weder der Aufrufer noch der
 * Benutzer eine Zahlungsart vorgibt.
 */
function preferredPaymentMethod(
  items: ReadonlyArray<Pick<PaymentItem, "paymentMethod">>,
  fallback: number
): number {
  const counts = new Map<number, number>();
  for (const item of items) {
    const method = Number(item.paymentMethod || 0);
    // Geplante Online-Zahlarten (z. B. PayPal) sind beim Kassieren nicht wählbar; sie fallen auf die Vorgabe zurück.
    if (method > 0 && MANUAL_PAYMENT_METHOD_TYPES.includes(method)) counts.set(method, (counts.get(method) || 0) + 1);
  }

  let preferred = fallback;
  let highestCount = 0;
  for (const [method, count] of counts) {
    if (count > highestCount) {
      preferred = method;
      highestCount = count;
    }
  }
  return preferred;
}

export type PaymentItem = {
  /** Fachliche Vorauswahl aus dem Kontoauszugs-Endpunkt, auch nach manueller Personenwahl. */
  suggested?: boolean;
  id: string;
  description: string;
  dueDate: string;
  openAmount: number;
  paymentMethod?: number | null;
  /** Zusätzliche Felder je Herkunft (Beitrag, offener Posten, Artikelverkauf). */
  [key: string]: unknown;
};

/** Werte des Zahlungsformulars, wie antd sie liefert. */
export type PaymentDrawerFormValues = {
  amount?: number | null;
  differenceHandling?: string | null;
  method?: number | null;
  reference?: string | null;
} & Record<string, unknown>;

export type PaymentDrawerSubmitValues = {
  amount: number;
  paidDate: string | null;
  method: number | null;
  reference: string | null;
};

export type PaymentDrawerProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSuccess?: () => void;
  items: PaymentItem[];
  selectedItems: PaymentItem[];
  onSelectedItemsChange: (items: PaymentItem[]) => void;
  paymentEndpoint?: (itemId: string) => string; // Standard: (id) => `/api/v1/open-items/${id}/pay`
  /** Gleiche Zahlungsoberfläche mit abweichender Fachaktion, z. B. Meldung an einen Inkassofall. */
  submitPayment?: (values: PaymentDrawerSubmitValues, selectedItems: PaymentItem[]) => Promise<void>;
  successMessageKey?: string;
  allowOverpayment?: boolean;
  /**
   * Fachlich vorgegebene Zahlungsart (z. B. Meldung an einen Inkassofall). Ist sie gesetzt, greift
   * weder die gemerkte Zahlungsart des Benutzers noch wird die Auswahl als solche gemerkt.
   */
  defaultPaymentMethod?: number;
  fetchAvailableItems?: () => Promise<PaymentItem[]>; // Für automatische Item-Ladung
  columns?: ListColumn<PaymentItem>[]; // Optionale Spalten für die Item-Tabelle
  itemType?: "contribution" | "openItem" | "mixed"; // Für spezielle Logik
  personId: string;
  /** Optional: Heimatstudio der Person – dann wird das pro Studio konfigurierte Guthabenkonto verwendet. */
  studioId?: string | null;
  /** Bankumsatz: fester Eingangsbetrag, Datum/Referenz vorbelegt, atomarer Verarbeitungsmarker im Bulk-Pfad. */
  bankTransaction?: { bankAccountId: string; externalId: string; amount: number; rememberPayer?: boolean; expectedRemainingAmount?: number };
  /** Explizite Teilbuchung desselben Bankumsatzes; jede Teilbuchung bleibt im normalen Zahlungspfad. */
  allowPartialBankPayment?: boolean;
  onBankPaymentRecorded?: (amount: number) => void | Promise<void>;
  contentLoading?: boolean;
  initialPayment?: { paidDate: string; reference: string };
  suggestedItemId?: string | null;
  suggestedItemIds?: string[] | null;
  /** Ohne übergebene Auswahl die als `suggested` gelieferten Posten vorauswählen (fällig und kassierbar). */
  preselectSuggestedItems?: boolean;
  /** Inhalt über der Postenauswahl, z. B. die Wahl des Mitglieds beim Verbuchen eines Bankumsatzes. */
  header?: React.ReactNode;
};

export const PaymentDrawer: React.FC<PaymentDrawerProps> = ({
  open,
  title,
  onClose,
  onSuccess,
  items: initialItems,
  selectedItems: initialSelectedItems,
  onSelectedItemsChange,
  paymentEndpoint,
  submitPayment,
  successMessageKey = "Payment(s) recorded.",
  allowOverpayment = true,
  defaultPaymentMethod,
  fetchAvailableItems,
  columns: customColumns,
  itemType = "mixed",
  personId,
  studioId: studioIdProp,
  bankTransaction,
  allowPartialBankPayment = false,
  onBankPaymentRecorded,
  contentLoading = false,
  initialPayment,
  suggestedItemId,
  suggestedItemIds,
  preselectSuggestedItems = false,
  header
}) => {
  const feedback = useFeedback();
  const auth = useAuth();
  const { text } = useI18n();
  const units = loadUnits();
  const [form] = Form.useForm<{
    amount: number;
    paidDate?: Dayjs;
    method?: number;
    reference?: string;
    differenceHandling?: string;
    walletId?: string;
    overpaymentMethod?: string;
  }>();
  
  const [saving, setSaving] = React.useState(false);
  const watchedAmount = Form.useWatch("amount", form);
  const [availableItems, setAvailableItems] = React.useState<PaymentItem[]>(initialItems);
  const [selectedItems, setSelectedItems] = React.useState<PaymentItem[]>(initialSelectedItems);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<string[]>(initialSelectedItems.map(i => i.id));
  const { dirty: paymentDirty, resetDirty: resetPaymentDirty, sync: syncPayment } = useFormDirty(form);
  const { dirty: selectionDirty, markSaved: markSelectionSaved } = useDirtySnapshot([...selectedRowKeys].sort());
  const [payWallets, setPayWallets] = React.useState<Array<{ id: string; name: string; balance: number; type: number; walletProductId?: string | null }>>([]);
  const [defaultWalletProductId, setDefaultWalletProductId] = React.useState<string | null>(null);
  const [availableItemsLoadFailed, setAvailableItemsLoadFailed] = React.useState(false);
  // Zahlarten über eine Abfrage statt State plus Effekt: Der Ladezustand ist damit abgeleitet
  // statt gesetzt (kein synchrones setState im Effekt-Rumpf), und die Liste kommt beim erneuten
  // Öffnen aus dem Cache.
  const paymentMethodDefsQuery = useQuery({
    queryKey: ["lookups", "payment-method-definitions", "with-id"],
    queryFn: async () =>
      (await auth.api.get<Array<{ id: string; paymentMethodType: number; name: string; isActive?: boolean }>>(
        "/api/v1/payment-method-definitions"
      )) || [],
    enabled: open
  });
  const paymentMethodDefs = React.useMemo(
    () => paymentMethodDefsQuery.data ?? [],
    [paymentMethodDefsQuery.data]
  );
  const methodsLoading = open && paymentMethodDefsQuery.isPending;

  const paymentMethodSelectOptions = React.useMemo(
    () => paymentMethodDefinitionSelectOptions(paymentMethodDefs, true),
    [paymentMethodDefs]
  );

  const { rememberPaymentMethod, resolveDefaultPaymentMethod } = useLastPaymentMethod();

  /**
   * Vorbelegung der Zahlungsart, sofern eine gilt: zuerst die fachliche Vorgabe des Aufrufers,
   * sonst die zuletzt vom Benutzer selbst erfasste Zahlungsart — aber nur, solange der Mandant sie
   * noch anbietet. Ohne beides bleibt es bei der Ableitung aus den gewählten Posten.
   */
  const presetPaymentMethod =
    defaultPaymentMethod ?? resolveDefaultPaymentMethod(paymentMethodSelectOptions, null);

  /** Hat der Benutzer die Zahlungsart im offenen Drawer selbst gewählt? Dann nicht mehr vorbelegen. */
  const methodTouchedRef = React.useRef(false);
  
  const dateInputFormat = useDateInputFormat();
  const decimalSeparator = decimalSeparatorOf(units);
  const currencyCode = (units.currency || units.currencyCode || "").trim() || "EUR";
  const moneyPlaceholder = moneyPlaceholderOf(units);
  
  // Beim Öffnen laufen drei unabhängige Abrufe: Zahlarten (oben als Abfrage), Wertkonten und
  // offene Posten. Jeder füllt ein anderes Bedienelement, und ohne gemeinsames Kennzeichen füllten
  // sie sich einzeln im offenen Drawer nach. Zusammengefasst zu `contentLoading`.
  const [walletsLoading, setWalletsLoading] = React.useState(false);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  
  // Lade Wertekonten und Standard-Guthabenkonto
  React.useEffect(() => {
    // Ohne Person (Kontoauszug: Mitglied noch nicht gewählt) gibt es keine Wertekonten zu laden.
    if (open && !submitPayment && personId) {
      setPayWallets([]);
      setDefaultWalletProductId(null);
      setWalletsLoading(true);
      Promise.all([
        auth.api.get<Array<{ id: string; walletProductId?: string | null; walletProductNameSnapshot: string; balance: number; walletTypeSnapshot: number }>>(`/api/v1/wallets?personId=${encodeURIComponent(personId)}`),
        auth.api.get<string | null>(
          studioIdProp
            ? `/api/v1/settings/default-wallet-product-for-payment?studioId=${encodeURIComponent(studioIdProp)}`
            : "/api/v1/settings/default-wallet-product-for-payment"
        ).catch(() => null)
      ]).then(([wallets, defaultWalletProductIdFromApi]) => {
        const creditWallets = (wallets || []).filter(w => Number(w.walletTypeSnapshot || 0) === WALLET_TYPE_VALUE);
        const walletList = creditWallets.map(w => ({
          id: w.id,
          name: w.walletProductNameSnapshot,
          balance: Number(w.balance || 0),
          type: Number(w.walletTypeSnapshot || 0),
          walletProductId: w.walletProductId || null
        }));
        
        const defaultId = defaultWalletProductIdFromApi ?? null;
        setDefaultWalletProductId(defaultId);
        
        // Wenn Standard-Konto in Einstellungen existiert, prüfe ob bereits ein Wallet mit diesem Produkt existiert
        if (defaultId) {
          const hasWalletWithDefaultProduct = walletList.some(w => 
            w.walletProductId === defaultId
          );
          
          // Wenn kein Wallet mit Standard-Produkt existiert, füge es zur Liste hinzu
          if (!hasWalletWithDefaultProduct) {
            // Lade WalletProduct-Info für Standard-Konto
            auth.api.get<{ id: string; name: string }>(`/api/v1/wallet-products/${encodeURIComponent(defaultId)}`)
              .then((product) => {
                if (product) {
                  walletList.push({
                    id: `default-${product.id}`, // Virtuelle ID für Standard-Konto
                    name: `${product.name} (Standard)`,
                    balance: 0,
                    type: WALLET_TYPE_VALUE,
                    walletProductId: product.id
                  });
                }
                setPayWallets(walletList);
              })
              .catch(() => {
                setPayWallets(walletList);
              });
          } else {
            setPayWallets(walletList);
          }
        } else {
          setPayWallets(walletList);
        }
      }).catch(() => {
        setPayWallets([]);
        setDefaultWalletProductId(null);
      }).finally(() => setWalletsLoading(false));
    }
  }, [open, personId, studioIdProp, auth, submitPayment]);
  
  // Übergebene Items übernehmen, sobald der Parent sie liefert (z. B. nach async Laden beim Öffnen aus dem Beiträge-Tab)
  React.useEffect(() => {
    if (open && initialItems.length > 0) {
      setAvailableItems(initialItems);
    }
  }, [open, initialItems]);

  // Lade verfügbare Items beim Öffnen
  React.useEffect(() => {
    if (!open) {
      // Reset when drawer closes
      setSelectedItems([]);
      setSelectedRowKeys([]);
      setAvailableItemsLoadFailed(false);
      return;
    }
    setAvailableItemsLoadFailed(false);
    // Ein Personenwechsel im Kontoauszug darf keine Auswahl der vorherigen Person behalten.
    if (bankTransaction) {
      setAvailableItems([]);
      setSelectedItems([]);
      setSelectedRowKeys([]);
    }
    if (initialPayment) form.setFieldsValue({ paidDate: dayjs(initialPayment.paidDate), reference: initialPayment.reference });
    if (bankTransaction) form.setFieldValue("amount", bankTransaction.amount);
    methodTouchedRef.current = false;
    let active = true;
    markSelectionSaved(bankTransaction ? [] : initialSelectedItems.map(item => item.id).sort());
    // Sofort übergebene Items anzeigen, damit keine "keine offenen Posten verfügbar"-Anzeige beim Öffnen erscheint
    if (initialItems.length > 0) {
      setAvailableItems(initialItems);
    }
    // Sofort übergebene Auswahl übernehmen, damit sie beim Öffnen sichtbar ist (nicht erst nach fetchAvailableItems)
    if (!bankTransaction && initialSelectedItems.length > 0) {
      setSelectedItems(initialSelectedItems);
      setSelectedRowKeys(initialSelectedItems.map(i => i.id));
      const totalAmount = initialSelectedItems.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
      form.setFieldValue("amount", totalAmount);
    }
    form.setFieldValue(
      "method",
      presetPaymentMethod ?? preferredPaymentMethod(initialSelectedItems, PAYMENT_METHOD_DEFAULT)
    );
    if (fetchAvailableItems) {
      setItemsLoading(true);
      fetchAvailableItems().then((items) => {
        if (!active) return;
        setAvailableItemsLoadFailed(false);
        if (items.length > 0 || initialItems.length === 0) {
          setAvailableItems(items);
        }
        // Stelle sicher, dass ausgewählte Items auch in der Liste sind (nach Fetch ggf. anpassen)
        const useSuggestedFlags = !!bankTransaction || (preselectSuggestedItems && initialSelectedItems.length === 0);
        const suggestedIds = new Set(useSuggestedFlags ? items.filter(item => item.suggested).map(item => item.id)
          : suggestedItemIds ?? (suggestedItemId ? [suggestedItemId] : []));
        const validSelected = suggestedIds.size > 0
          ? items.filter(item => suggestedIds.has(item.id))
          : bankTransaction ? [] : initialSelectedItems.filter(selected => items.some(item => item.id === selected.id));
        if (validSelected.length > 0) {
          setSelectedItems(validSelected);
          setSelectedRowKeys(validSelected.map(i => i.id));
          const totalAmount = validSelected.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
          form.setFieldValue("amount", bankTransaction?.amount ?? totalAmount);
          
          if (presetPaymentMethod == null && !methodTouchedRef.current) {
            form.setFieldValue("method", preferredPaymentMethod(validSelected, PAYMENT_METHOD_DEFAULT));
          }
          markSelectionSaved(validSelected.map(item => item.id).sort());
        }
        resetPaymentDirty();
      }).catch(() => {
        if (active) setAvailableItemsLoadFailed(true);
      }).finally(() => {
        if (active) setItemsLoading(false);
      });
    } else {
      setAvailableItems(initialItems);
      // Wenn initialSelectedItems vorhanden sind, setze sie
      if (initialSelectedItems.length > 0) {
        setSelectedItems(initialSelectedItems);
        setSelectedRowKeys(initialSelectedItems.map(i => i.id));
        const totalAmount = initialSelectedItems.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
        form.setFieldValue("amount", bankTransaction?.amount ?? totalAmount);
      }
    }
    resetPaymentDirty();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, personId, bankTransaction?.amount]); // Öffnen, Personenwechsel oder neuer Bankrest nach Teilbuchung; nicht bei jeder Änderung der Auswahl.

  // Die Zahlarten des Mandanten kommen asynchron; ob die gemerkte Zahlungsart noch angeboten wird,
  // steht beim Öffnen deshalb noch nicht fest. Nachziehen, solange der Benutzer die Auswahl nicht
  // selbst angefasst hat.
  React.useEffect(() => {
    if (!open || presetPaymentMethod == null || methodTouchedRef.current) return;
    syncPayment({ ...form.getFieldsValue(true), method: presetPaymentMethod });
  }, [open, presetPaymentMethod, form, syncPayment]);
  
  // Update selected items when row selection changes - removed to prevent infinite loop
  // onSelectedItemsChange is now called directly in the rowSelection onChange handler
  
  const defaultColumns: ListColumn<PaymentItem>[] = React.useMemo(() => [
    {
      title: text("Description"),
      dataIndex: "description",
      key: "description",
      width: DESCRIPTION_COLUMN_WIDTH,
      ellipsis: true,
      sorter: (a, b) => String(a.description || "").localeCompare(String(b.description || "")),
      render: (_: unknown, r: PaymentItem) => r.description || "—"
    },
    {
      title: text("Due date"),
      dataIndex: "dueDate",
      key: "dueDate",
      width: DUE_DATE_COLUMN_WIDTH,
      ellipsis: true,
      sorter: (a, b) => String(a.dueDate || "").localeCompare(String(b.dueDate || "")),
      render: (_: unknown, r: PaymentItem) => formatDateOnly(String(r.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
    },
    {
      title: itemType === "contribution" ? text("Contribution") : itemType === "openItem" ? text("Amount") : text("Open amount"),
      dataIndex: "openAmount",
      key: "openAmount",
      width: AMOUNT_COLUMN_WIDTH,
      ellipsis: true,
      align: "right",
      sorter: (a, b) => Number(a.openAmount || 0) - Number(b.openAmount || 0),
      render: (_: unknown, r: PaymentItem) => formatMoney(Number(r.openAmount || 0), units)
    }
  ], [text, units, itemType]);
  
  const columns = customColumns || defaultColumns;
  
  const handleFinish = async (values: PaymentDrawerFormValues) => {
    if (!values.amount || values.amount <= 0) {
      feedback.warning(text("Please enter an amount."));
      return;
    }
    
    setSaving(true);
    try {
      const paidDateIso = values.paidDate ? formatDateOnlyIso(values.paidDate) : null;
      const totalAmount = Number(values.amount || 0);
      if (bankTransaction && totalAmount > bankTransaction.amount + MONEY_EPSILON) {
        feedback.warning(text("Bank statement amount exceeds remainder"));
        return;
      }
      
      // Ohne Auswahl ist der gesamte Betrag eine Überzahlung. Die Behandlung bestimmt
      // das Ziel; verfügbare Beiträge dürfen nicht stillschweigend ausgewählt werden.
      const rowsToProcess = selectedItems;
      if (rowsToProcess.length === 0 && (!allowOverpayment || submitPayment)) {
        feedback.warning(text("No open items found."));
        setSaving(false);
        return;
      }
      
      const totalOpenAmount = rowsToProcess.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
      const difference = totalAmount - totalOpenAmount;
      const hasDifference = Math.abs(difference) > MONEY_EPSILON;

      if (!allowOverpayment && difference > MONEY_EPSILON) {
        feedback.warning(text("Payment amount must not exceed the outstanding amount."));
        setSaving(false);
        return;
      }

      if (submitPayment) {
        await submitPayment({
          amount: totalAmount,
          paidDate: paidDateIso,
          method: values.method || null,
          reference: values.reference?.trim() || null
        }, rowsToProcess);
        if (defaultPaymentMethod == null) rememberPaymentMethod(values.method);
        feedback.success(text(successMessageKey));
        onClose();
        form.resetFields();
        setSelectedItems([]);
        setSelectedRowKeys([]);
        onSuccess?.();
        return;
      }
      
      // Prüfe auf Überzahlung
      if (hasDifference && difference > 0) {
        if (!values.differenceHandling) {
          values.differenceHandling = DIFFERENCE_HANDLING_NEXT_DUE;
        }
        if (values.differenceHandling === DIFFERENCE_HANDLING_NEXT_DUE && !values.overpaymentMethod) {
          values.overpaymentMethod = values.method || null;
        }
      }
      
      // Validierung für Überzahlung
      if (hasDifference && difference > 0) {
        if (!values.differenceHandling) {
          feedback.warning(text("Please select a handling method for the overpayment."));
          setSaving(false);
          return;
        }
        if (values.differenceHandling === DIFFERENCE_HANDLING_WALLET && !defaultWalletProductId) {
          // Kein Standard-Guthabenkonto konfiguriert – Überzahlung kann nicht gutgeschrieben
          // werden. Auf "nächste Fälligkeiten" zurückfallen statt zu blockieren. (Die Option
          // wird in diesem Fall ohnehin nicht angeboten.)
          values.differenceHandling = DIFFERENCE_HANDLING_NEXT_DUE;
          values.overpaymentMethod = values.method || null;
        }
        if (values.differenceHandling === DIFFERENCE_HANDLING_NEXT_DUE && !values.overpaymentMethod) {
          values.overpaymentMethod = values.method || null;
        }
      }
      
      const paymentLines: Array<{ item: PaymentItem; amount: number }> = [];
      
      // Intelligente Verteilung bei Unterzahlung oder wenn keine Vorselektion
      if (hasDifference && difference < 0 || selectedItems.length === 0) {
        // Der Kontoauszug liefert bereits die fachliche Reihenfolge: Referenzen zuerst, dann älteste Fälligkeit.
        const sortedItems = bankTransaction ? rowsToProcess : [...rowsToProcess].sort((a, b) => {
          const dateA = String(a.dueDate || "").slice(0, ISO_DATE_LENGTH);
          const dateB = String(b.dueDate || "").slice(0, ISO_DATE_LENGTH);
          if (dateA !== dateB) {
            return dateA.localeCompare(dateB);
          }
          const amountA = Number(a.openAmount || 0);
          const amountB = Number(b.openAmount || 0);
          return amountA - amountB;
        });
        
        let remainingAmount = totalAmount;
        for (const item of sortedItems) {
          if (remainingAmount <= MONEY_EPSILON) break;
          const openAmount = Number(item.openAmount || 0);
          const amountForThis = Math.min(remainingAmount, openAmount);
          paymentLines.push({ item, amount: amountForThis });
          remainingAmount -= amountForThis;
        }
      } else {
        // Exakte Zahlung oder Ueberzahlung: ausgewaehlte Positionen zuerst voll begleichen.
        for (const item of rowsToProcess) {
          const openAmount = Number(item.openAmount || 0);
          if (openAmount <= MONEY_EPSILON) continue;
          paymentLines.push({ item, amount: openAmount });
        }
      }

      let walletCreditAmount = 0;
      let walletIdForBulk: string | null = null;
      let walletProductIdForBulk: string | null = null;

      // Differenz-Behandlung bei Überzahlung vorbereiten; der Bulk-Endpunkt verbucht sie atomar mit den Zahlungen.
      if (hasDifference && difference > 0) {
        if (values.differenceHandling === DIFFERENCE_HANDLING_NEXT_DUE && (itemType === "contribution" || itemType === "mixed")) {
          const openItemsList = await auth.api.get<Array<{ id: string; status?: number; amount?: number; paidAmount?: number; openAmount?: number; dueDate?: string }>>(`/api/v1/open-items?personId=${encodeURIComponent(personId)}`);
          const unpaid = (openItemsList || [])
            .filter((oi) => {
              const status = Number(oi.status ?? 0);
              const openAmt = Number(oi.openAmount ?? (Number(oi.amount || 0) - Number(oi.paidAmount || 0)));
              const isAlreadySelected = rowsToProcess.some(r => r.id === oi.id);
              return status === OPEN_ITEM_STATUS_OPEN && openAmt > MONEY_EPSILON && !isAlreadySelected;
            })
            .sort((a, b) => String(a.dueDate || "").localeCompare(String(b.dueDate || "")));
          let remainingDifference = difference;
          for (const oi of unpaid) {
            if (remainingDifference <= MONEY_EPSILON) break;
            const openAmt = Number(oi.openAmount ?? (Number(oi.amount || 0) - Number(oi.paidAmount || 0)));
            const amountToApply = Math.min(remainingDifference, openAmt);
            paymentLines.push({
              item: {
                id: String(oi.id),
                description: "",
                dueDate: String(oi.dueDate || "").slice(0, ISO_DATE_LENGTH),
                openAmount: openAmt,
                type: "openItem"
              },
              amount: amountToApply
            });
            remainingDifference -= amountToApply;
          }
        } else if (values.differenceHandling === DIFFERENCE_HANDLING_WALLET && defaultWalletProductId) {
          // Überzahlung wird immer dem konfigurierten Standard-Guthabenkonto gutgeschrieben –
          // es gibt keine manuelle Konto-Auswahl. Vorhandenes Wallet mit diesem Produkt
          // weiterführen, sonst beim Buchen neu anlegen.
          walletCreditAmount = difference;
          const existingWallet = payWallets.find(w =>
            !w.id.startsWith("default-") && w.walletProductId === defaultWalletProductId
          );
          if (existingWallet) {
            walletIdForBulk = existingWallet.id;
          } else {
            walletProductIdForBulk = defaultWalletProductId;
          }
        }
      }

      const bulkItems = paymentLines
        .map(({ item, amount }) => ({
          openItemId: String(item.openItemId || (item.type === ITEM_TYPE_CONTRIBUTION ? "" : item.id)),
          amount
        }))
        .filter((line) => line.openItemId && line.amount > MONEY_EPSILON);
      const canUseBulk = bulkItems.length === paymentLines.length;

      // Ohne Auswahl darf der neue Erfassungspfad nicht ohne vollständige Zuordnung
      // erfolgreich enden, etwa wenn es keine nächsten Fälligkeiten gibt.
      const allocatedAmount = paymentLines.reduce((sum, line) => sum + line.amount, 0) + walletCreditAmount;
      if ((selectedItems.length === 0 || bankTransaction) && Math.abs(totalAmount - allocatedAmount) > MONEY_EPSILON) {
        feedback.warning(text("The payment amount could not be fully allocated. Select more items or transfer the overpayment to a credit wallet."));
        return;
      }

      if (canUseBulk) {
        await auth.api.post("/api/v1/open-items/pay-bulk", {
          items: bulkItems,
          personId: bulkItems.length === 0 ? personId : undefined,
          paidDate: paidDateIso || undefined,
          method: values.method || null,
          reference: values.reference || null,
          walletId: walletIdForBulk,
          walletProductId: walletProductIdForBulk,
          walletCreditAmount: walletCreditAmount > MONEY_EPSILON ? walletCreditAmount : null,
          bankTransaction: bankTransaction ? { ...bankTransaction,
            amount: totalAmount,
            // Eine Teilbuchung darf keine dauerhafte Zahlerzuordnung erzeugen.
            rememberPayer: bankTransaction.rememberPayer && Math.abs(totalAmount - bankTransaction.amount) <= MONEY_EPSILON
          } : undefined
        });
      } else {
        for (const { item, amount } of paymentLines) {
          if (!paymentEndpoint) throw new Error("Payment endpoint missing");
          await auth.api.post(paymentEndpoint(item.id), {
            amount,
            paidDate: paidDateIso || undefined,
            method: values.method || null,
            reference: values.reference || null
          });
        }

        if (walletCreditAmount > MONEY_EPSILON) {
          if (walletIdForBulk) {
            const wallet = payWallets.find(w => w.id === walletIdForBulk);
            if (wallet) {
              await auth.api.put(`/api/v1/wallets/${encodeURIComponent(walletIdForBulk)}`, {
                balance: wallet.balance + walletCreditAmount
              });
            }
          } else if (walletProductIdForBulk) {
            const today = dayjs().format("YYYY-MM-DD");
            await auth.api.post("/api/v1/wallets", {
              personId: personId,
              walletProductId: walletProductIdForBulk,
              balance: walletCreditAmount,
              validFrom: today,
              allowsAccess: false,
              isActive: true,
              requiresNoArrears: true,
              accessScope: 0,
              allowedStudioIds: []
            });
          }
        }
      }
      
      if (defaultPaymentMethod == null) rememberPaymentMethod(values.method);
      feedback.success(text(successMessageKey));
      if (bankTransaction && onBankPaymentRecorded) {
        await onBankPaymentRecorded(totalAmount);
        return;
      }
      onClose();
      form.resetFields();
      setSelectedItems([]);
      setSelectedRowKeys([]);
      onSuccess?.();
    } catch {
      // Central API toast shows error
    } finally {
      setSaving(false);
    }
  };
  
  const handleClose = () => {
    onClose();
    form.resetFields();
    setSelectedItems([]);
    setSelectedRowKeys([]);
  };

  return (
    <>
      {!open ? <Form form={form} style={{ display: "none" }} /> : null}
      <FormDrawer
        title={title}
        dirty={paymentDirty || selectionDirty}
        titleNode={title}
        open={open}
        onCancel={handleClose}
        formId="payment-drawer-form"
        submitting={saving}
        contentLoading={contentLoading || methodsLoading || walletsLoading || itemsLoading}
        submitDisabled={contentLoading || !(Number(watchedAmount) > 0) || (!!bankTransaction && (!personId || availableItemsLoadFailed || Number(watchedAmount) > bankTransaction.amount + MONEY_EPSILON)) || (selectedItems.length === 0 && (!allowOverpayment || !!submitPayment))}
        submitText={allowPartialBankPayment && bankTransaction && Number(watchedAmount) < bankTransaction.amount - MONEY_EPSILON ? "Bank statement record partial payment" : "Record payment"}
      >
      <Form
        id="payment-drawer-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
      >
        {header}
        <DrawerSelectionList<PaymentItem>
          title={itemType === "openItem" ? text("Select open items") : itemType === "contribution" ? text("Select contributions") : text("Select items")}
          dataSource={availableItems}
          columns={columns}
          rowKey="id"
          emptyText={
            <Typography.Text type="secondary">
              {availableItemsLoadFailed
                ? text("Error loading items")
                : itemType === "contribution"
                  ? text("No contributions available")
                  : itemType === "openItem"
                    ? text("No open items available")
                    : text("No items available")}
            </Typography.Text>
          }
          rowSelection={{
              selectedRowKeys,
              hideSelectAll: false,
              selectionByRowClick: true,
              onChange: (selectedKeys, selectedRows) => {
                setSelectedRowKeys(selectedKeys as string[]);
                setSelectedItems(selectedRows);
                onSelectedItemsChange(selectedRows);
                const totalAmount = selectedRows.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
                if (!bankTransaction || !allowPartialBankPayment) form.setFieldValue("amount", bankTransaction?.amount ?? totalAmount);

                // Gilt eine Vorgabe oder die gemerkte Zahlungsart, bleibt die Auswahl stehen; sonst
                // leitet sie sich weiterhin aus den gewählten Posten ab.
                if (presetPaymentMethod == null && !methodTouchedRef.current && selectedRows.length > 0) {
                  form.setFieldValue("method", preferredPaymentMethod(selectedRows, PAYMENT_METHOD_DEFAULT));
                }
              }
            }}
          />

        <Form.Item
          label={text("Payment amount")}
          required
        >
          <Space.Compact style={{ width: "100%", display: "flex" }}>
            <Form.Item
              name="amount"
              noStyle
              rules={[{ required: true, message: text("Amount is required") }]}
            >
              <InputNumber
                style={{ flex: 1, minWidth: 0 }}
                readOnly={!!bankTransaction && !allowPartialBankPayment}
                max={bankTransaction?.amount}
                min={0.01}
                step={0.01}
                precision={2}
                decimalSeparator={decimalSeparator}
                placeholder={moneyPlaceholder}
                onChange={(value) => {
                  const total = selectedItems.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
                  const diff = (value || 0) - total;
                  if (diff > MONEY_EPSILON) {
                    if (!form.getFieldValue("differenceHandling")) {
                      // Standardmäßig "nextDue" für contributions/mixed, "wallet" für openItems
                      // (nur wenn ein Standard-Guthabenkonto konfiguriert ist).
                      if (itemType === "contribution" || itemType === "mixed") {
                        form.setFieldValue("differenceHandling", "nextDue");
                      } else if (defaultWalletProductId) {
                        form.setFieldValue("differenceHandling", "wallet");
                      }
                    }
                  } else {
                    form.setFieldValue("differenceHandling", null);
                  }
                }}
              />
            </Form.Item>
            <Input
              aria-hidden="true"
              readOnly
              tabIndex={-1}
              value={currencyCode}
              style={{ width: 64, textAlign: "center", pointerEvents: "none" }}
            />
          </Space.Compact>
        </Form.Item>
        
        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.amount !== currentValues.amount
            || prevValues.differenceHandling !== currentValues.differenceHandling
          }
          noStyle
        >
          {() => {
            if (selectedItems.length === 0 && (!allowOverpayment || submitPayment)) return null;
            const selected = selectedItems;
            const total = selected.reduce((sum, r) => sum + Number(r.openAmount || 0), 0);
            const currentAmount = form.getFieldValue("amount") || 0;
            const difference = currentAmount - total;
            const hasDifference = Math.abs(difference) > MONEY_EPSILON;
          
          if (hasDifference && difference > 0) {
            return (
              <>
                <Form.Item
                  name="differenceHandling"
                  label={text("Handle overpayment")}
                  rules={[{ required: true, message: text("Please select a handling method") }]}
                  extra={
                    <Typography.Text type="success">
{`${text("Overpayment")}: ${formatMoney(difference, units)}`}
                    </Typography.Text>
                  }
                >
                  <Select
                    placeholder={text("Select handling…")}
                    options={[
                      // "nextDue" nur für contributions und mixed (nicht für reine openItems)
                      ...(itemType === "contribution" || itemType === "mixed" ? [{ value: "nextDue", label: text("Apply to next due items") }] : []),
                      // "wallet" nur, wenn ein Standard-Guthabenkonto konfiguriert ist – es gibt
                      // genau ein festes Zielkonto, daher keine manuelle Auswahl.
                      ...(defaultWalletProductId ? [{ value: "wallet", label: text("Transfer to credit wallet") }] : [])
                    ]}
                  />
                </Form.Item>
              </>
            );
          }
          if (hasDifference && difference < 0) {
            return (
              <Form.Item
                label={text("Underpayment")}
              >
                <Typography.Text type="warning">
                  {`${formatMoney(Math.abs(difference), units)}. ${text("Underpayment amount distributed")}`}
                </Typography.Text>
              </Form.Item>
            );
          }
          return null;
          }}
        </Form.Item>
        
        <Form.Item
          name="paidDate"
          label={text("Payment date")}
          rules={[
            { required: true, message: text("Payment date is required") },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const selectedDate = dayjs(value);
                const today = dayjs().startOf("day");
                if (selectedDate.isAfter(today, "day")) {
                  return Promise.reject(new Error(text("Payment date must not be in the future")));
                }
                return Promise.resolve();
              }
            }
          ]}
          initialValue={dayjs().startOf("day")}
        >
          <DatePicker
            style={{ width: "100%" }}
            format={dateInputFormat}
            allowClear={false}
            disabledDate={(current) => {
              if (!current) return false;
              return current.isAfter(dayjs().endOf("day"), "day");
            }}
          />
        </Form.Item>
        
        <Form.Item
          name="method"
          label={text("Payment method")}
          rules={[{ required: true, message: text("Payment method is required") }]}
          initialValue={presetPaymentMethod ?? PAYMENT_METHOD_DEFAULT}
        >
          <Select
            options={paymentMethodSelectOptions}
            placeholder={text("Select payment method…")}
            onChange={() => {
              methodTouchedRef.current = true;
            }}
          />
        </Form.Item>
        
        <Form.Item
          name="reference"
          label={text("Reference")}
        >
          <Input placeholder={text("Optional")} />
        </Form.Item>
      </Form>
      </FormDrawer>
    </>
  );
};
